import * as yup from 'yup';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { TypInterwalu } from 'api/autoExport/autoExport.enum';

const MAX_ADDRESS_LENGTH = 253;
const MAX_USERNAME_LENGTH = 100;
const MAX_PASSWORD_LENGTH = 100;
const MAX_DIRECTORY_LENGTH = 100;
const MAX_FILE_NAME_LENGTH = 50;
const MIN_PORT = 1;
const MAX_PORT = 65535;
const MAX_PORT_LENGTH = 5;
const MIN_INTERVAL_MINUTES = 10;
const MAX_INTERVAL_MINUTES = 480;

const getTimeKey = (value: unknown): string | null => {
  if (!(value instanceof Date) || isNaN(value.getTime())) return null;

  return `${value.getHours()}:${value.getMinutes()}`;
};

const isUniqueTime = (value: unknown, otherFields: unknown[]): boolean => {
  const key = getTimeKey(value);
  if (!key) return true;

  return otherFields.every((other) => {
    const otherKey = getTimeKey(other);

    return !otherKey || otherKey !== key;
  });
};

export const useAutomationFormValidation = () => {
  const { formatMessage } = useLocale();

  const schema = yup.object().shape({
    adres: yup
      .string()
      .required(formatMessage({ id: AppMessages['common.validation.required'] }))
      .max(MAX_ADDRESS_LENGTH, formatMessage({ id: AppMessages['definitions.automation.validation.address.max'] })),

    port: yup
      .string()
      .required(formatMessage({ id: AppMessages['common.validation.required'] }))
      .max(MAX_PORT_LENGTH, formatMessage({ id: AppMessages['definitions.automation.validation.port.max'] }))
      .test(
        'port-range',
        formatMessage({ id: AppMessages['definitions.automation.validation.port.range'] }),
        (value) => {
          if (!value) return false;
          const num = parseInt(value, 10);
          return !isNaN(num) && num >= MIN_PORT && num <= MAX_PORT;
        },
      ),

    nazwaUzytkownika: yup
      .string()
      .required(formatMessage({ id: AppMessages['common.validation.required'] }))
      .max(MAX_USERNAME_LENGTH, formatMessage({ id: AppMessages['definitions.automation.validation.username.max'] })),

    haslo: yup
      .string()
      .max(MAX_PASSWORD_LENGTH, formatMessage({ id: AppMessages['definitions.automation.validation.password.max'] })),

    katalog: yup
      .string()
      .max(MAX_DIRECTORY_LENGTH, formatMessage({ id: AppMessages['definitions.automation.validation.directory.max'] })),

    formatTyp: yup.string().required(formatMessage({ id: AppMessages['common.validation.required'] })),

    nazwaPliku: yup
      .string()
      .required(formatMessage({ id: AppMessages['common.validation.required'] }))
      .max(MAX_FILE_NAME_LENGTH, formatMessage({ id: AppMessages['definitions.automation.validation.fileName.max'] })),

    typInterwalu: yup.string().required(formatMessage({ id: AppMessages['common.validation.required'] })),

    interwalMinuty: yup.string().when('typInterwalu', {
      is: TypInterwalu.INTERWAL,
      then: (schema) =>
        schema
          .required(formatMessage({ id: AppMessages['common.validation.required'] }))
          .test(
            'interval-range',
            formatMessage({ id: AppMessages['definitions.automation.validation.interval.range'] }),
            (value) => {
              if (!value) return false;
              const num = parseInt(value, 10);
              return !isNaN(num) && num >= MIN_INTERVAL_MINUTES && num <= MAX_INTERVAL_MINUTES;
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    harmonogramCzas1: yup
      .mixed()
      .nullable()
      .when('typInterwalu', {
        is: TypInterwalu.HARMONOGRAM,
        then: (schema) =>
          schema
            .test(
              'valid-time',
              formatMessage({ id: AppMessages['common.validation.required'] }),
              (value) => value instanceof Date && !isNaN(value.getTime()),
            )
            .test(
              'unique-time',
              formatMessage({ id: AppMessages['definitions.automation.validation.schedule.unique'] }),
              function (value) {
                return isUniqueTime(value, [this.parent.harmonogramCzas2, this.parent.harmonogramCzas3]);
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    harmonogramCzas2: yup
      .mixed()
      .nullable()
      .when('typInterwalu', {
        is: TypInterwalu.HARMONOGRAM,
        then: (schema) =>
          schema
            .test(
              'valid-time',
              formatMessage({ id: AppMessages['common.validation.required'] }),
              (value) => value instanceof Date && !isNaN(value.getTime()),
            )
            .test(
              'unique-time',
              formatMessage({ id: AppMessages['definitions.automation.validation.schedule.unique'] }),
              function (value) {
                return isUniqueTime(value, [this.parent.harmonogramCzas1, this.parent.harmonogramCzas3]);
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    harmonogramCzas3: yup
      .mixed()
      .nullable()
      .test(
        'unique-time',
        formatMessage({ id: AppMessages['definitions.automation.validation.schedule.unique'] }),
        function (value) {
          return isUniqueTime(value, [this.parent.harmonogramCzas1, this.parent.harmonogramCzas2]);
        },
      ),

    stempelDaty: yup.boolean().required(),

    rcpFiltr: yup.string().required(formatMessage({ id: AppMessages['common.validation.required'] })),

    komorki: yup.array().of(yup.object().shape({ id: yup.string().required(), nazwa: yup.string().notRequired() })),
  });

  return { schema };
};
